import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Box,
  Text,
  Icon,
  HStack,
  Badge,
} from "@chakra-ui/react";
import { FaUserCheck, FaPodcast } from "react-icons/fa";
import { useMutation } from "@apollo/client";
import { UPDATE_CLIENT } from "../queries";
import type { CaptureUserDetailsModalProps, UserDetailsFormData } from "../types";
import { getColor, brandConfig } from "../../../brandConfig";

export const CaptureUserDetailsModal: React.FC<CaptureUserDetailsModalProps> = ({
  isOpen,
  onClose,
  userId,
  onUpdateSuccess,
  currentUserData
}) => {
  const [formData, setFormData] = useState<UserDetailsFormData>({
    firstName: currentUserData?.fName || "",
    lastName: currentUserData?.lName || "",
    email: currentUserData?.email || "",
  });
  const toast = useToast();

  const [updateClient, { loading }] = useMutation(UPDATE_CLIENT);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      const { data: _data } = await updateClient({
        variables: {
          id: userId,
          input: {
            fName: formData.firstName,
            lName: formData.lastName,
            email: formData.email,
          }
        }
      });

      toast({
        title: "Success! 🎉",
        description: "Your profile has been updated. You now have access to exclusive podcast content!",
        status: "success",
        duration: 5000,
      });

      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update your profile",
        status: "error",
        duration: 5000,
      });
    }
  };

  // Consistent styling
  const cardBorder = getColor("border.darkCard");
  const textPrimary = getColor("text.primaryDark");
  const textSecondary = getColor("text.secondaryDark");
  const textMuted = getColor("text.mutedDark");
  const accentPurple = "#9333EA";

  // Don't show modal if we already have the data
  if (currentUserData?.fName && currentUserData?.lName && currentUserData?.email) {
    console.log('✅ User already has profile data, not showing modal');
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} closeOnEsc={false} size="lg">
      <ModalOverlay bg="rgba(0, 0, 0, 0.7)" backdropFilter="blur(10px)" />
      <ModalContent
        bg="rgba(30, 30, 30, 0.98)"
        backdropFilter="blur(20px)"
        borderColor={cardBorder}
        border="1px solid"
        boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
      >
        <ModalHeader borderBottom="1px" borderColor={cardBorder}>
          <VStack align="start" spacing={2}>
            <HStack>
              <Icon as={FaUserCheck} color={accentPurple} />
              <Text color={textPrimary} fontFamily={brandConfig.fonts.heading}>
                Complete Your Profile
              </Text>
            </HStack>
            <Badge 
              bg={`${accentPurple}20`}
              color={accentPurple}
              fontSize="sm" 
              px={3} 
              py={1}
              borderRadius="full"
            >
              <HStack spacing={1}>
                <Icon as={FaPodcast} boxSize={3} />
                <Text>Get Instant Podcast Access</Text>
              </HStack>
            </Badge>
          </VStack>
        </ModalHeader>
        <ModalCloseButton 
          color={textSecondary}
          _hover={{ color: textPrimary, bg: "rgba(255, 255, 255, 0.1)" }}
        />
        <ModalBody pb={6} pt={6}>
          <VStack align="stretch" spacing={4}>
            <Text color={textSecondary} fontSize="md">
              Complete your profile to unlock exclusive podcast content and receive notifications when new episodes are released.
            </Text>
            
            {/* Debug info - show client ID if available */}
            {(userId || currentUserData) && (
              <Box 
                p={3} 
                bg="rgba(147, 51, 234, 0.1)" 
                borderRadius="md" 
                border="1px solid"
                borderColor={`${accentPurple}40`}
              >
                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" color={textMuted}>Client Information:</Text>
                  {userId && (
                    <Text fontSize="xs" color={textSecondary}>
                      ID: {userId}
                    </Text>
                  )}
                  {currentUserData?.fName && currentUserData?.lName && (
                    <Text fontSize="xs" color={textSecondary}>
                      Current Name: {currentUserData.fName} {currentUserData.lName}
                    </Text>
                  )}
                  {currentUserData?.email && (
                    <Text fontSize="xs" color={textSecondary}>
                      Current Email: {currentUserData.email}
                    </Text>
                  )}
                </VStack>
              </Box>
            )}
          </VStack>
          <VStack spacing={4} mt={4}>
            <FormControl isRequired>
              <FormLabel color={textPrimary}>First Name<Text as="span" color="red.400">*</Text></FormLabel>
              <Input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
                bg="rgba(0, 0, 0, 0.2)"
                border="1px"
                borderColor={cardBorder}
                color={textPrimary}
                borderRadius="lg"
                _placeholder={{ color: textMuted }}
                _focus={{
                  borderColor: accentPurple,
                  boxShadow: `0 0 0 1px ${accentPurple}40`
                }}
                _hover={{
                  borderColor: textSecondary
                }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textPrimary}>Last Name<Text as="span" color="red.400">*</Text></FormLabel>
              <Input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                bg="rgba(0, 0, 0, 0.2)"
                border="1px"
                borderColor={cardBorder}
                color={textPrimary}
                borderRadius="lg"
                _placeholder={{ color: textMuted }}
                _focus={{
                  borderColor: accentPurple,
                  boxShadow: `0 0 0 1px ${accentPurple}40`
                }}
                _hover={{
                  borderColor: textSecondary
                }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textPrimary}>Email<Text as="span" color="red.400">*</Text></FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                bg="rgba(0, 0, 0, 0.2)"
                border="1px"
                borderColor={cardBorder}
                color={textPrimary}
                borderRadius="lg"
                _placeholder={{ color: textMuted }}
                _focus={{
                  borderColor: accentPurple,
                  boxShadow: `0 0 0 1px ${accentPurple}40`
                }}
                _hover={{
                  borderColor: textSecondary
                }}
              />
            </FormControl>

            <Button
              bg={accentPurple}
              color="white"
              _hover={{ 
                bg: "#7C3AED",
                transform: "translateY(-2px)"
              }}
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Saving..."
              w="full"
              mt={4}
              size="lg"
              boxShadow="0 2px 4px rgba(147, 51, 234, 0.2)"
              leftIcon={<Icon as={FaPodcast} />}
            >
              Save and Get Podcast Access
            </Button>

            <Text fontSize="xs" color={textMuted} textAlign="center" mt={2}>
              By completing your profile, you'll receive email notifications about new podcast episodes and exclusive content.
            </Text>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};